import React from "react";

import {
  ArrowLeft,
  BatteryFull,
  Check,
  CheckCircle2,
  Clock3,
  Home,
  IdCard,
  Lock,
  MoreHorizontal,
  Plus,
  QrCode,
  ScanLine,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  Signal,
  SwitchCamera,
  User,
  Wallet,
  Wifi,
} from "lucide-react";

import CredIssuerLogo from "../../../images/Favicon.svg";

// Screen labels mirror the CredIssuer Wallet app, so they stay untranslated.
const PLACEHOLDER_CARDS = [
  {
    id: "placeholder-1",
    icon: IdCard,
    thumbClass: "from-sky-400 to-indigo-500",
    titleWidth: "w-[70%]",
  },
  {
    id: "placeholder-2",
    icon: User,
    thumbClass: "from-amber-300 to-orange-400",
    titleWidth: "w-[55%]",
  },
  {
    id: "placeholder-3",
    icon: User,
    thumbClass: "from-slate-700 to-slate-900",
    titleWidth: "w-[80%]",
  },
];

const NAV_ITEMS = [
  { label: "Home", icon: Home },
  { label: "Share", icon: Share2 },
  { label: "History", icon: Clock3 },
  { label: "Settings", icon: Settings },
];

const PhoneFrame = ({ children, className = "" }) => (
  <div
    aria-hidden="true"
    className={`relative w-[168px] select-none rounded-[28px] bg-gray-900 p-[5px] shadow-[0_22px_45px_-20px_rgba(16,24,40,0.55)] ${className}`}
  >
    <div className="relative flex h-[330px] flex-col overflow-hidden rounded-[23px] bg-white">
      <span className="absolute left-1/2 top-[6px] z-10 h-[9px] w-[42px] -translate-x-1/2 rounded-full bg-gray-900" />
      <div className="flex items-center justify-between px-[0.75rem] pb-1 pt-[7px] text-[7px] font-semibold text-gray-900">
        <span>5:25</span>
        <span className="flex items-center gap-[2px]">
          <Signal className="h-[7px] w-[7px]" strokeWidth={3} />
          <Wifi className="h-[7px] w-[7px]" strokeWidth={3} />
          <BatteryFull className="h-[8px] w-[8px]" strokeWidth={2.5} />
        </span>
      </div>
      {children}
    </div>
  </div>
);

const BottomNav = () => (
  <div className="mt-auto grid grid-cols-4 border-t border-gray-100 bg-white px-1 pb-2 pt-1.5">
    {NAV_ITEMS.map(({ label, icon: Icon }, index) => (
      <span
        key={label}
        className={`flex flex-col items-center gap-[2px] text-[6px] font-medium ${
          index === 0 ? "text-cfPrimary" : "text-gray-400"
        }`}
      >
        <Icon className="h-[9px] w-[9px]" strokeWidth={2.5} />
        {label}
      </span>
    ))}
  </div>
);

const ScreenHeader = ({ title }) => (
  <div className="relative flex items-center justify-center px-2.5 py-1.5">
    <span className="absolute left-2.5 flex h-[14px] w-[14px] items-center justify-center rounded-full bg-gray-100 text-gray-600">
      <ArrowLeft className="h-[8px] w-[8px]" strokeWidth={2.5} />
    </span>
    <span className="text-[8px] font-semibold text-gray-900">{title}</span>
  </div>
);

const CardThumb = ({ logoUrl, thumbClass }) =>
  logoUrl ? (
    <span className="flex h-[14px] w-[18px] items-center justify-center overflow-hidden rounded-[3px] bg-white ring-1 ring-gray-200">
      <img src={logoUrl} alt="" className="h-full w-full object-contain" />
    </span>
  ) : (
    <span
      className={`h-[14px] w-[18px] rounded-[3px] bg-gradient-to-br ${thumbClass}`}
    />
  );

const CardRow = ({
  title,
  titleWidth,
  icon: Icon,
  thumbClass,
  logoUrl,
  isNew = false,
}) => (
  <div
    className={`flex items-center gap-1.5 rounded-md px-1.5 py-[5px] ${
      isNew
        ? "bg-cfTableHeaderBackgroundColor ring-1 ring-cfPrimary"
        : "bg-white ring-1 ring-gray-100"
    }`}
  >
    <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
      <Icon className="h-[8px] w-[8px]" strokeWidth={2.5} />
    </span>
    <span className="min-w-0 flex-1">
      {title ? (
        <span className="block truncate text-[7px] font-semibold leading-tight text-gray-900">
          {title}
        </span>
      ) : (
        <span
          className={`my-[2px] block h-[4px] rounded-full bg-gray-200 ${titleWidth}`}
        />
      )}
      <span className="flex items-center gap-[2px] text-[6px] font-medium leading-tight text-emerald-600">
        <CheckCircle2 className="h-[6px] w-[6px]" strokeWidth={3} />
        Valid
      </span>
    </span>
    <CardThumb logoUrl={logoUrl} thumbClass={thumbClass} />
    <ShieldCheck
      className="h-[8px] w-[8px] shrink-0 text-emerald-500"
      strokeWidth={2.5}
    />
    <MoreHorizontal className="h-[8px] w-[8px] shrink-0 text-gray-400" />
  </div>
);

const WalletHomeScreen = ({ cards, highlightFab = false }) => (
  <>
    <div className="px-2.5 py-1.5">
      <img src={CredIssuerLogo} alt="" className="h-[9px] w-auto" />
    </div>
    <div className="mx-2.5 flex h-[18px] items-center gap-1 rounded-md px-1.5 text-[7px] text-gray-400 ring-1 ring-gray-200">
      <Search className="h-[7px] w-[7px]" strokeWidth={2.5} />
      Search
    </div>
    <p className="mb-1 mt-2 px-2.5 text-[7px] font-semibold text-gray-700">
      {cards.length} cards
    </p>
    <div className="flex flex-col gap-1 px-2">
      {cards.map(({ id, ...card }) => (
        <CardRow key={id} {...card} />
      ))}
    </div>
    <div className="absolute bottom-[40px] right-2.5">
      {highlightFab ? (
        <span className="absolute -inset-[6px] rounded-full border-[1.5px] border-dashed border-cfPrimary motion-safe:animate-pulse" />
      ) : null}
      <span className="relative flex h-[26px] w-[26px] items-center justify-center rounded-full bg-cfPrimary text-white shadow-[0_6px_12px_-4px_var(--cf-primary-color)]">
        <Plus className="h-[12px] w-[12px]" strokeWidth={3} />
      </span>
    </div>
    <BottomNav />
  </>
);

const HintArrow = ({ className = "" }) => (
  <svg
    viewBox="0 0 40 24"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M38 20C28 21 14 18 6 6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M3 11L5.5 4.5L12 6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const OpenWalletIllustration = ({ hint }) => (
  <div aria-hidden="true" className="relative">
    <PhoneFrame>
      <WalletHomeScreen cards={PLACEHOLDER_CARDS} highlightFab />
    </PhoneFrame>
    {hint ? (
      <span className="absolute bottom-[78px] left-[calc(100%-2px)] flex w-[78px] flex-col items-start text-cfPrimary">
        <span className="text-[11px] font-medium italic leading-tight">
          {hint}
        </span>
        <HintArrow className="mt-1 h-4 w-8 -scale-x-100 rotate-[200deg]" />
      </span>
    ) : null}
  </div>
);

export const AddCardIllustration = ({ hint }) => (
  <PhoneFrame>
    <ScreenHeader title="Add new card" />
    <div className="mx-2.5 mt-2 flex items-center gap-1.5 rounded-lg border-[1.5px] border-dashed border-cfPrimary bg-cfTableHeaderBackgroundColor px-2 py-2">
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md bg-white text-cfPrimary">
        <ScanLine className="h-[10px] w-[10px]" strokeWidth={2.5} />
      </span>
      <span className="min-w-0">
        <span className="block text-[7px] font-semibold leading-tight text-gray-900">
          Scan &amp; Download Card
        </span>
        <span className="block text-[5.5px] leading-tight text-gray-500">
          Scan a QR code to instantly download your card
        </span>
      </span>
    </div>
    {hint ? (
      <div className="mt-1 flex flex-col items-center text-cfPrimary">
        <HintArrow className="h-5 w-9 rotate-[25deg]" />
        <span className="text-[11px] font-medium italic">{hint}</span>
      </div>
    ) : null}
    <BottomNav />
  </PhoneFrame>
);

const CORNERS = [
  "left-0 top-0 border-l-2 border-t-2 rounded-tl-md",
  "right-0 top-0 border-r-2 border-t-2 rounded-tr-md",
  "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-md",
  "bottom-0 right-0 border-b-2 border-r-2 rounded-br-md",
];

export const ScanIllustration = () => (
  <PhoneFrame>
    <ScreenHeader title="Download Card" />
    <div className="relative mx-2.5 mt-1 h-[176px] overflow-hidden rounded-lg bg-gradient-to-br from-slate-600 via-slate-700 to-slate-900">
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_55%)]" />
      <div className="absolute left-1/2 top-1/2 h-[92px] w-[92px] -translate-x-1/2 -translate-y-1/2">
        {CORNERS.map((corner) => (
          <span
            key={corner}
            className={`absolute h-[16px] w-[16px] border-white ${corner}`}
          />
        ))}
        <QrCode
          className="absolute left-1/2 top-1/2 h-[52px] w-[52px] -translate-x-1/2 -translate-y-1/2 text-white/80"
          strokeWidth={1.5}
        />
        <span className="absolute inset-x-1 top-1/2 h-[2px] rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.7)] motion-safe:animate-wallet-scan" />
      </div>
    </div>
    <div className="mt-2.5 flex flex-col items-center gap-[3px] text-[6px] font-medium text-gray-600">
      <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full text-cfPrimary ring-1 ring-gray-200">
        <SwitchCamera className="h-[10px] w-[10px]" strokeWidth={2.5} />
      </span>
      Flip Camera
    </div>
    <BottomNav />
  </PhoneFrame>
);

const CONFETTI = [
  "left-[-18px] top-[36px] h-[3px] w-[12px] rotate-[25deg]",
  "left-[-10px] top-[58px] h-[3px] w-[9px] -rotate-[15deg]",
  "right-[-22px] top-[54px] h-[3px] w-[12px] -rotate-[30deg]",
  "right-[-14px] top-[74px] h-[3px] w-[8px] rotate-[20deg]",
];

export const CredentialAddedIllustration = ({ credentialName, logoUrl }) => (
  <div aria-hidden="true" className="relative">
    <PhoneFrame>
      <WalletHomeScreen
        cards={[
          {
            id: "new-credential",
            title: credentialName,
            titleWidth: "w-[65%]",
            icon: IdCard,
            logoUrl,
            isNew: true,
          },
          ...PLACEHOLDER_CARDS,
        ]}
      />
    </PhoneFrame>
    <span
      className="absolute -right-[14px] -top-[14px] flex h-10 w-10 animate-cert-card-in items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_8px_18px_-6px_rgba(16,185,129,0.8)] ring-4 ring-white"
    >
      <Check className="h-5 w-5" strokeWidth={3} />
    </span>
    {CONFETTI.map((piece) => (
      <span
        key={piece}
        className={`absolute rounded-full bg-cfPrimaryLight opacity-50 ${piece}`}
      />
    ))}
  </div>
);

const HighlightRing = ({ className = "" }) => (
  <span
    className={`pointer-events-none absolute rounded-lg border-[1.5px] border-dashed border-cfPrimary motion-safe:animate-pulse ${className}`}
  />
);

const BrowserBar = () => (
  <div className="mx-2.5 mb-1 flex h-[16px] items-center justify-center gap-[3px] rounded-md bg-gray-100 text-[6px] font-medium text-gray-500">
    <Lock className="h-[6px] w-[6px]" strokeWidth={3} />
    credissuer.com
  </div>
);

const IssuerAvatar = ({ logoUrl, className = "" }) => (
  <span
    className={`flex shrink-0 items-center justify-center overflow-hidden rounded-[4px] bg-white ring-1 ring-gray-200 ${className}`}
  >
    {logoUrl ? (
      <img src={logoUrl} alt="" className="h-full w-full object-contain" />
    ) : (
      <IdCard className="h-2/3 w-2/3 text-cfPrimary" strokeWidth={2.5} />
    )}
  </span>
);

const OfferPagePreview = ({
  credentialName,
  issuerName,
  logoUrl,
  buttonLabel,
  highlightButton = false,
}) => (
  <div className="flex flex-col items-center px-[0.75rem] pt-2">
    <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md bg-cfPrimary text-white">
      <Wallet className="h-[11px] w-[11px]" strokeWidth={2.5} />
    </span>
    <span className="mt-1.5 text-center text-[8px] font-bold leading-tight text-gray-900">
      Add your credential to CredIssuer Wallet
    </span>
    <div className="mt-2 w-full rounded-lg bg-white p-1.5 ring-1 ring-gray-200">
      <div className="flex items-center gap-1">
        <IssuerAvatar logoUrl={logoUrl} className="h-[14px] w-[14px]" />
        <span className="truncate text-[6.5px] font-semibold text-gray-700">
          {issuerName}
        </span>
      </div>
      <div className="mt-1 rounded-md bg-gray-50 px-1.5 py-1">
        <span className="block text-[5px] font-semibold uppercase tracking-wider text-cfPrimary">
          Credential
        </span>
        <span className="block truncate text-[7.5px] font-bold text-gray-900">
          {credentialName}
        </span>
      </div>
    </div>
    <div className="relative mt-2 w-full">
      {highlightButton ? <HighlightRing className="-inset-[4px]" /> : null}
      <span className="flex items-center justify-center gap-1 rounded-md bg-cfPrimary py-1.5 text-[6.5px] font-semibold text-white shadow-[0_6px_12px_-6px_var(--cf-primary-color)]">
        <Wallet className="h-[8px] w-[8px]" strokeWidth={2.5} />
        {buttonLabel}
      </span>
    </div>
  </div>
);

export const TapAddButtonIllustration = ({
  credentialName,
  issuerName,
  logoUrl,
  buttonLabel,
  hint,
}) => (
  <PhoneFrame>
    <BrowserBar />
    <OfferPagePreview
      credentialName={credentialName}
      issuerName={issuerName}
      logoUrl={logoUrl}
      buttonLabel={buttonLabel}
      highlightButton
    />
    {hint ? (
      <div className="mt-1.5 flex flex-col items-center text-cfPrimary">
        <HintArrow className="h-5 w-9 rotate-[25deg]" />
        <span className="text-[11px] font-medium italic">{hint}</span>
      </div>
    ) : null}
  </PhoneFrame>
);

export const OpenInWalletIllustration = ({
  credentialName,
  issuerName,
  logoUrl,
  buttonLabel,
  hint,
}) => (
  <PhoneFrame>
    <div className="relative flex-1">
      <BrowserBar />
      <OfferPagePreview
        credentialName={credentialName}
        issuerName={issuerName}
        logoUrl={logoUrl}
        buttonLabel={buttonLabel}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/40 px-[0.75rem]">
        <div className="w-full overflow-hidden rounded-xl bg-white text-center shadow-[0_12px_24px_-10px_rgba(16,24,40,0.6)]">
          <div className="px-2 pb-2 pt-2.5">
            <span className="mx-auto flex h-[20px] w-[20px] items-center justify-center rounded-md bg-cfPrimary text-white">
              <Wallet className="h-[10px] w-[10px]" strokeWidth={2.5} />
            </span>
            <span className="mt-1.5 block text-[7.5px] font-semibold leading-tight text-gray-900">
              Open in “CredIssuer Wallet”?
            </span>
          </div>
          <div className="grid grid-cols-2 border-t border-gray-200 text-[7.5px]">
            <span className="border-r border-gray-200 py-1.5 text-gray-500">
              Cancel
            </span>
            <span className="relative py-1.5 font-semibold text-cfPrimary">
              <HighlightRing className="inset-[2px]" />
              Open
            </span>
          </div>
        </div>
        {hint ? (
          <div className="mt-1.5 flex flex-col items-center text-white">
            <HintArrow className="h-5 w-9 -scale-x-100 rotate-[-25deg]" />
            <span className="text-[11px] font-medium italic">{hint}</span>
          </div>
        ) : null}
      </div>
    </div>
  </PhoneFrame>
);

export const ReviewOfferIllustration = ({
  credentialName,
  issuerName,
  logoUrl,
  hint,
}) => (
  <PhoneFrame>
    <ScreenHeader title="Download Card" />
    <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-1">
      <div className="relative overflow-hidden rounded-lg bg-cfPrimary p-2 text-white shadow-[0_8px_16px_-8px_var(--cf-primary-color)]">
        <span className="absolute -right-4 -top-6 h-16 w-16 rounded-full bg-white/10" />
        <div className="relative flex items-center gap-1">
          <IssuerAvatar logoUrl={logoUrl} className="h-[14px] w-[14px]" />
          <span className="truncate text-[6px] font-medium opacity-90">
            {issuerName}
          </span>
        </div>
        <span className="relative mt-2 block truncate text-[9px] font-bold">
          {credentialName}
        </span>
        <span className="relative mt-1 inline-flex items-center gap-[2px] rounded-full bg-white/20 px-1.5 py-[1px] text-[5.5px] font-semibold">
          <ShieldCheck className="h-[6px] w-[6px]" strokeWidth={3} />
          Verified issuer
        </span>
      </div>
      <div className="mt-2 divide-y divide-gray-100 rounded-lg ring-1 ring-gray-100">
        {[
          ["Issued by", issuerName, "text-gray-900"],
          ["Card", credentialName, "text-gray-900"],
          ["Status", "Valid", "text-emerald-600"],
        ].map(([label, value, valueClass]) => (
          <div
            key={label}
            className="flex items-center justify-between gap-2 px-2 py-[5px] text-[6.5px]"
          >
            <span className="text-gray-500">{label}</span>
            <span className={`truncate font-semibold ${valueClass}`}>
              {value}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-auto flex flex-col items-center">
        {hint ? (
          <div className="mb-1 flex flex-col items-center text-cfPrimary">
            <span className="text-[11px] font-medium italic">{hint}</span>
            <HintArrow className="h-5 w-9 rotate-[205deg]" />
          </div>
        ) : null}
        <div className="relative w-full">
          <HighlightRing className="-inset-[4px]" />
          <span className="flex items-center justify-center rounded-md bg-cfPrimary py-1.5 text-[7.5px] font-semibold text-white">
            Download
          </span>
        </div>
      </div>
    </div>
  </PhoneFrame>
);
